<template>
  <div class="container py-5">
    <div class="row justify-content-center mb-4" v-if="billingAccounts && billingAccounts.length > 0">
      <!-- Billing account selection -->
      <div class="col-md-4 pb-2">
        <select class="form-control" id="plan" v-model="selectedBillingAccountId" :disabled="loading">
          <option value="">Select a billing account</option>
          <option v-for="acc in billingAccounts" :key="acc.id" :value="acc.id">{{ acc.company_name }}</option>
        </select>
      </div>
    </div>

    <!-- No billing accounts -->
    <section v-else-if="!loading">
      <p>
        You do not have access to billing accounts. If you would like to contract with Enable Banking, please complete the form below so we can provide a tailored pricing quote and prepare an agreement for use of the Enable Banking API.
      </p>
      <div class="bg-light rounded p-3">
        <h4>Quote Request</h4>
        <QuoteForm />
      </div>
    </section>

    <!-- Billing account details - shown when a billing account is selected and loaded -->
    <div v-if="selectedBillingAccountId && selectedBillingAccount">
      <div class="card mb-4">
        <div class="card-header bg-light"><strong>Billing Account {{ selectedBillingAccount.id }}</strong></div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <p><strong>Company Name:</strong> {{ selectedBillingAccount.company_name }}</p>
              <p><strong>Company ID:</strong> {{ selectedBillingAccount.company_id }}</p>
            </div>
            <div class="col-md-6">
              <p><strong>VAT ID:</strong> {{ selectedBillingAccount.company_vat_id }}</p>
              <p><strong>VAT Rate:</strong> {{ selectedBillingAccount.company_vat_rate }}%</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card mb-4">
        <div class="card-header bg-light d-flex justify-content-between align-items-center">
          <strong>Active Subscriptions as of {{ selectedBillingAccount.current_date }}</strong>
          <button class="btn btn-sm btn-link" @click="toggleSubscriptions">
            <font-awesome-icon :icon="['fas', showSubscriptions ? 'chevron-up' : 'chevron-down']" />
          </button>
        </div>
        <div class="card-body" v-if="showSubscriptions">
          <div class="row" v-for="(s, index) in selectedBillingAccount.active_subscriptions" :key="s.id"
            :class="{'border-bottom pb-4 mb-4': index < selectedBillingAccount.active_subscriptions.length - 1}">
            <div class="col-md-6">
              <h5 class="mb-3">{{ s.name }}</h5>
              <p class="mb-2"><strong>Start Date:</strong> {{ s.start_date }}</p>
              <p class="mb-2"><strong>End Date:</strong> {{ s.end_date || 'Ongoing' }}</p>
              <p class="mb-2" v-if="s.applications">
                <strong>Linked applications:</strong> {{ s.applications.length }}
                <button v-if="s.applications.length > 0" class="btn btn-sm btn-outline-secondary ml-3"
                  @click="toggleApplications(s.id)">
                  {{ isApplicationsVisible(s.id) ? 'Hide' : 'Show' }}
                </button>
              </p>
            </div>
            <div class="col-md-6">
              <div class="border-start ps-3 mb-3">
                <div v-for="(tier, index) in s.pricing_tiers" :key="index" class="d-flex justify-content-between mb-2">
                  <span v-if="tier.max_amount">{{ parseInt(tier.min_amount) + 1 }} - {{ tier.max_amount }} {{
                    s.unit.toLowerCase() }}s: </span>
                  <span v-else>{{ parseInt(tier.min_amount) + 1 }}+ {{ s.unit.toLowerCase() }}s: </span>
                  <span class="fw-medium text-secondary">€{{ tier.price }} / {{ s.unit.toLowerCase() }}</span>
                </div>
              </div>
              <p class="mb-0" v-if="s.minimal_invoice_amount">
                <strong>Minimum Subscription Fee:</strong> €{{ s.minimal_invoice_amount }} / month
                <span v-if="getRelatedSubscriptions(s).length > 0">
                  (together with {{ getRelatedSubscriptions(s).map(s => s.name).join(', ') }})
                </span>
              </p>
            </div>

            <!-- Applications list (full width row) -->
            <div class="col-12 mt-3" v-if="s.applications && s.applications.length > 0 && isApplicationsVisible(s.id)">
              <div class="row">
                <div v-for="app in s.applications" :key="app.id" class="col-12 col-md-6 mb-3">
                  <div class="bg-light rounded p-3 mb-3">
                    <div class="mb-3 d-flex align-items-start justify-content-between">
                      <h6><b class="mr-2">{{ app.name }}</b><small>({{ app.id }})</small></h6>
                      <small class="badge" :class="app.active ? 'badge-success' : 'badge-secondary'">
                        {{ app.active ? 'Active' : 'Inactive' }}
                      </small>
                    </div>
                    <div class="text-muted small mb-2">
                      <strong>Environment:</strong>
                      {{ app.environment }}
                    </div>
                    <div class="text-muted small mb-2">
                      <strong>Services:</strong>
                      {{ app.services.map(s => {return {AIS: "Account information", PIS: "Payment initiation"}[s]}).join(' • ') }}
                    </div>
                    <div class="small text-muted mt-2" v-if="app.creator_email">
                      <strong>Created by:</strong>
                      {{ app.creator_email }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Invoices Section -->
      <div id="invoices" class="card mb-4">
        <div class="card-header bg-light d-flex justify-content-between align-items-center">
          <strong>Invoices</strong>
          <button class="btn btn-sm btn-link" @click="toggleInvoices">
            <font-awesome-icon :icon="['fas', showInvoices ? 'chevron-up' : 'chevron-down']" />
          </button>
        </div>
        <div class="card-body" v-if="showInvoices">
          <div v-if="invoices.length === 0" class="alert alert-info">
            No invoices found for this billing account.
          </div>
          <div v-else>
            <div class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Period</th>
                    <th>Created</th>
                    <th>Total Amount</th>
                    <th style="width: 55px;">&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="invoice in invoices">
                    <tr :key="invoice.id">
                      <td>{{ invoice.invoice_id }}</td>
                      <td>{{ invoice.period }}</td>
                      <td>{{ formatDate(invoice.created_time, false) }}</td>
                      <td>€{{ invoice.total_amount }}</td>
                      <td>
                        <button class="btn btn-sm btn-link float-right" @click="toggleInvoiceDetails(invoice.id)">
                          <font-awesome-icon
                            :icon="['fas', isInvoiceDetailsVisible(invoice.id) ? 'chevron-down' : 'chevron-right']" />
                        </button>
                      </td>
                    </tr>
                    <tr :key="'details-' + invoice.id" v-if="isInvoiceDetailsVisible(invoice.id)">
                      <td colspan="5" class="bg-light">
                        <div v-for="row in invoice.invoice_rows" :key="row.id" class="mb-3">
                          <div class="d-flex justify-content-between">
                            <strong>{{ row.title }}</strong>
                            <span>
                              {{ {'volume': 'Volume-based cost', 'minimum': 'Minimum usage fee', 'fixed': 'Fixed cost'}[row.kind] }}
                            </span>
                            <strong>€{{ row.total_amount }}</strong>
                          </div>
                          <div v-for="subs in row.subscriptions" class="p-3">
                            <div v-if="subs.applications && subs.applications.length">
                              <div v-for="app in subs.applications" :key="app.id" class="row">
                                <div class="col-8">{{ app.name }} ({{ app.id }})</div>
                                <div class="col-4 text-right">{{ app.units }} {{ `${subs.unit.toLowerCase()}s` }}
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-12 text-right">
                                  <strong>Total usage:</strong>
                                  {{ subs.applications.reduce((total, app) => total + parseInt(app.units), 0) }} {{ `${subs.unit.toLowerCase()}s` }}
                                </div>
                              </div>
                              <div class="row mt-2" v-if="subs.pricing_tiers && subs.pricing_tiers.length">
                                <div class="col-12">
                                  <div class="alert alert-light p-2 mb-0">
                                    <small class="d-block mb-1"><strong>Pricing tiers for {{ subs.name }}:</strong></small>
                                    <ul class="mb-0">
                                      <li v-for="(tier, index) in subs.pricing_tiers" :key="index" class="small">
                                        <span v-if="tier.max_amount">{{ parseInt(tier.min_amount) + 1 }} - {{ tier.max_amount }} {{ subs.unit.toLowerCase() }}s: €{{ tier.price }} / {{ subs.unit.toLowerCase() }}</span>
                                        <span v-else>{{ parseInt(tier.min_amount) + 1 }}+ {{ subs.unit.toLowerCase() }}s: €{{ tier.price }} / {{ subs.unit.toLowerCase() }}</span>
                                      </li>
                                    </ul>
                                    <small class="d-block mt-1" v-for="calculation in [calculateTieredCost(subs)]">
                                      <template v-if="calculation">
                                        <strong>Cost calculation:</strong>
                                        {{ calculation.explanation.map(e => `(${e})`).join(' + ') }}
                                        <strong>= €{{ calculation.totalCost.toFixed(2) }}</strong>
                                      </template>
                                      <template v-else>No calculations available.</template>
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div v-else>No billed applications.</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
            <div v-if="invoicesContinuationKey && !loading" class="text-center mt-3">
              <button class="btn btn-outline-primary" @click="loadMoreInvoices">
                {{ 'Load More' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading indicator -->
    <div v-if="loading">
      <LoaderPic />Loading...
    </div>

    <!-- No plan selected message -->
    <div v-if="!selectedBillingAccountId && billingAccounts && billingAccounts.length > 0" class="row">
      <div class="col-12">
        <div class="alert alert-info">
          Please select a billing account to view its details and invoices.
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { formatDate } from '../utils.js'
import axios from 'axios'
import QuoteForm from '../components/QuoteForm.vue'
import LoaderPic from '../components/elements/Loader/LoaderPic.vue'

export default {
  name: 'Billing',
  components: {
    LoaderPic,
    QuoteForm
  },
  data() {
    return {
      loading: false,
      selectedBillingAccountId: '',
      selectedBillingAccount: null,
      visibleApplications: {}, // Track which subscription's applications are visible
      showSubscriptions: true, // Control visibility of subscriptions list
      invoices: [], // Store fetched invoices
      invoicesContinuationKey: null, // For pagination
      showInvoices: true, // Control visibility of invoices list
      visibleInvoiceDetails: {} // Track which invoice details are visible
    }
  },
  computed: {
    ...mapGetters(['idToken', 'billingAccounts']),
    ...mapState({
      countriesList: (state) => state.countriesList,
      brokerNames: (state) => state.brokerOriginsNameMap
    })
  },
  created() {
    if (!this.billingAccounts) {
      this.loadBillingAccounts()
    }
  },
  watch: {
    selectedBillingAccountId(newValue) {
      // Clear visible applications when changing billing account
      this.visibleApplications = {}
      this.visibleInvoiceDetails = {}
      // Reset visibility states to true when changing billing account
      this.showSubscriptions = true
      this.showInvoices = true

      if (newValue) {
        this.fetchBillingAccountDetails(newValue)
      } else {
        // Reset data when no billing account is selected
        this.selectedBillingAccount = null
        this.invoices = []
        this.invoicesContinuationKey = null
      }
    }
  },
  methods: {
    ...mapActions(['fetchBillingAccounts']),
    async loadBillingAccounts() {
      this.loading = true
      try {
        await this.fetchBillingAccounts()
      } finally {
        this.loading = false
      }
    },

    async fetchBillingAccountDetails(accountId) {
      this.loading = true
      this.selectedBillingAccount = null
      // Reset invoices when changing billing account
      this.invoices = []
      this.invoicesContinuationKey = null

      try {
        const response = await axios({
          method: 'get',
          url: `/api/billing/accounts/${accountId}/details`,
          headers: {
            Authorization: 'Bearer ' + this.idToken.token
          }
        })

        this.selectedBillingAccount = response.data

        // After account details are loaded, fetch invoices
        this.fetchInvoices(accountId)
      } catch (error) {
        console.error('Error fetching billing account details:', error)
        this.$store.dispatch('pushAlertMessage', {
          message: 'Unable to fetch billing account details'
        })
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch invoices for the selected billing account
     * @param {String} accountId - The ID of the billing account
     * @param {String} continuationKey - Optional continuation key for pagination
     */
    async fetchInvoices(accountId, continuationKey = null) {
      this.loading = true

      try {
        let url = `/api/billing/invoices?account=${accountId}`
        if (continuationKey) {
          url += `&continuation_key=${continuationKey}`
        }

        const response = await axios({
          method: 'get',
          url: url,
          headers: {
            Authorization: 'Bearer ' + this.idToken.token
          }
        })

        if (continuationKey) {
          // Append to existing invoices for pagination
          this.invoices = [...this.invoices, ...response.data.invoices]
        } else {
          // Replace invoices for initial load
          this.invoices = response.data.invoices || []
        }

        // Store continuation key for pagination if present
        this.invoicesContinuationKey = response.data.continuation_key || null
      } catch (error) {
        console.error('Error fetching invoices:', error)
        this.$store.dispatch('pushAlertMessage', {
          message: 'Unable to fetch invoices'
        })
      } finally {
        this.loading = false
      }
    },

    /**
     * Load more invoices using the continuation key
     */
    loadMoreInvoices() {
      this.fetchInvoices(this.selectedBillingAccountId, this.invoicesContinuationKey)
    },

    /**
     * Get other subscriptions that share the same minimal_invoice_id
     * @param {Object} subscription - The current subscription
     * @returns {Array} - Array of subscription objects that share the same minimal_invoice_id
     */
    getRelatedSubscriptions(subscription) {
      // Find other subscriptions with the same minimal_invoice_id
      return this.selectedBillingAccount.active_subscriptions
        .filter(s =>
          // Different subscription but same minimal_invoice_id
          s.id !== subscription.id &&
          s.minimal_invoice_id === subscription.minimal_invoice_id
        ) // Return the subscription objects
    },

    /**
     * Toggle visibility of applications for a subscription
     * @param {String} subscriptionId - The ID of the subscription
     */
    toggleApplications(subscriptionId) {
      // Toggle visibility state
      this.$set(this.visibleApplications, subscriptionId,
        !this.isApplicationsVisible(subscriptionId))
    },

    /**
     * Check if applications for a subscription are visible
     * @param {String} subscriptionId - The ID of the subscription
     * @returns {Boolean} - Whether applications are visible
     */
    isApplicationsVisible(subscriptionId) {
      return !!this.visibleApplications[subscriptionId]
    },

    /**
     * Toggle visibility of the subscriptions list
     */
    toggleSubscriptions() {
      this.showSubscriptions = !this.showSubscriptions
    },

    /**
     * Toggle visibility of the invoices list
     */
    toggleInvoices() {
      this.showInvoices = !this.showInvoices
    },

    /**
     * Toggle visibility of invoice details
     * @param {String} invoiceId - The ID of the invoice
     */
    toggleInvoiceDetails(invoiceId) {
      this.$set(this.visibleInvoiceDetails, invoiceId,
        !this.isInvoiceDetailsVisible(invoiceId))
    },

    /**
     * Check if invoice details are visible
     * @param {String} invoiceId - The ID of the invoice
     * @returns {Boolean} - Whether invoice details are visible
     */
    isInvoiceDetailsVisible(invoiceId) {
      return !!this.visibleInvoiceDetails[invoiceId]
    },

    /**
     * Format date string for display
     * @param {String} dateString - ISO date string
     * @returns {String} - Formatted date string
     */
    formatDate,

    /**
     * Calculate the cost based on tiered pricing
     * @param {Object} subscription - The subscription with pricing tiers and usage
     */
    calculateTieredCost(subscription) {
      if (!subscription.pricing_tiers || !subscription.applications) {
        return
      }

      // Calculate total units across all applications
      const totalUnits = subscription.applications.reduce((total, app) => total + parseInt(app.units || 0), 0)

      if (totalUnits === 0) {
        return
      }

      // Sort tiers by min_amount to ensure proper calculation
      const sortedTiers = [...subscription.pricing_tiers].sort((a, b) =>
        parseInt(a.min_amount) - parseInt(b.min_amount)
      )

      let remainingUnits = totalUnits
      let totalCost = 0
      const explanation = []

      // Calculate cost for each tier
      for (const tier of sortedTiers) {
        const minAmount = parseInt(tier.min_amount)
        const maxAmount = tier.max_amount ? parseInt(tier.max_amount) : Infinity
        const price = parseFloat(tier.price)

        // Units that fall within this tier
        const unitsInTier = Math.min(
          remainingUnits,
          maxAmount - minAmount
        )

        if (unitsInTier <= 0) {
          continue
        }

        const tierCost = unitsInTier * price
        totalCost += tierCost

        explanation.push(
          `${unitsInTier} ${subscription.unit.toLowerCase()}s * €${price} = €${tierCost.toFixed(2)}`
        )

        remainingUnits -= unitsInTier

        if (remainingUnits <= 0) {
          break
        }
      }

      return {
        totalCost: totalCost,
        explanation: explanation
      }
    }
  }
}
</script>

<style scoped>
.form-label {
  margin-bottom: 0.5rem;
}

.card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  word-wrap: break-word;
  background-color: #fff;
  background-clip: border-box;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.25rem;
}

.card-header {
  padding: 0.75rem 1.25rem;
  margin-bottom: 0;
}

.card-body {
  flex: 1 1 auto;
  padding: 1.25rem;
  border-top: 1px solid rgba(0, 0, 0, 0.125);
}

#invoices table thead th {
  border-top-width: 0;
}
</style>
